import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { TankColumn, ComplianceTable, getParameterStatus } from "../components/acms/Shared";
import { Badge } from "../components/ui/badge";
import { Droplets } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea, CartesianGrid, Legend } from "recharts";

function Card({ title, accent, children, testid }) {
  return (
    <div className="glass p-5 rounded-lg" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs uppercase tracking-wider text-gray-600 font-bold">{title}</h3>
        {accent && <Badge className={`border-0 ${accent === "OK" ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : accent === "WARNING" ? "bg-amber-50 text-amber-700" : "bg-red-50 border border-red-200 text-red-700"}`}>{accent}</Badge>}
      </div>
      {children}
    </div>
  );
}

const statusLabel = (pct) => {
  // GR threshold: ≥ 50% COMPLIANT, 30-50% WARNING, <30% CRITICAL
  if (pct >= 75) return "OK";
  if (pct >= 50) return "OK";
  if (pct >= 30) return "WARNING";
  return "CRITICAL";
};

export default function TankPage() {
  const { selectedSite, sites } = useSiteContext();
  const [tele, setTele] = useState(null);
  const [hist, setHist] = useState([]);
  const [siteCfg, setSiteCfg] = useState(null);
  const [tanks, setTanks] = useState([]);
  const [liveValues, setLiveValues] = useState({});

  useEffect(() => {
    if (!selectedSite) return;
    const load = () => {
      api.get(`/telemetry/${selectedSite}`).then((r) => setTele(r.data)).catch(() => {});
      api.get(`/assets`, { params: { site_id: selectedSite, asset_type: "tank" } }).then((r) => setTanks(r.data)).catch(() => {});
      api.get(`/assets/live-values`, { params: { site_id: selectedSite } }).then((r) => setLiveValues(r.data || {})).catch(() => {});
    };
    load();
    api.get(`/site-config-full/${selectedSite}`).then((r) => setSiteCfg(r.data)).catch(() => {});
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [selectedSite]);

  // 24h trend for the first gateway-linked tank — real register history instead of the
  // old hardcoded /telemetry/history feed (which the chart below never actually plotted).
  const trendTank = tanks.find((t) => t.gateway_id && t.io_point_name);
  useEffect(() => {
    if (!selectedSite || !trendTank) { setHist([]); return; }
    let cancelled = false;
    const load = () => {
      api.get(`/v1/sites/${selectedSite}/telemetry/parameter-history`, { params: { parameter: trendTank.io_point_name, hours: 720 } })
        .then((r) => {
          if (cancelled) return;
          setHist((r.data || []).map((d) => ({ ...d, day: new Date(d.ingested_at || d.timestamp).toLocaleDateString([], { month: "short", day: "numeric" }) })));
        }).catch(() => {});
    };
    load();
    const i = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(i); };
  }, [selectedSite, trendTank]);


  const site = sites.find((s) => s.site_id === selectedSite);

  return (
    <div className="space-y-6" data-testid="tank-page">
      <div>
        <div className="text-xs text-gray-500">Dashboard → {site?.name} → Fire Water Tank</div>
        <h1 className="text-3xl md:text-4xl font-extrabold">Fire Water Tank</h1>
        <p className="text-sm text-gray-500 mt-1">GR Appendix c · manage registered tanks · auto-refresh 5s</p>
      </div>

      {/* REGISTERED TANKS */}
      {tanks.length > 0 && (
        <section data-testid="registered-tanks-section">
          <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-3 flex items-center gap-2">
            <Droplets className="w-4 h-4 text-orange-400" strokeWidth={2.5} /> Registered Tanks
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tanks.map((tank) => {
              const capacity = Number(tank.meta?.capacity_litres || 25000);
              const live = liveValues[tank.asset_id];
              const level = live ? Number(live.value) : null;
              return (
                <Card key={tank.asset_id} title={`${tank.name}`} accent={level !== null ? statusLabel(level) : undefined} testid={`card-tank-${tank.asset_id}`}>
                  <div className="flex items-center justify-between gap-4">
                    {level !== null ? (
                      <TankColumn value={level} capacityLitres={capacity} />
                    ) : (
                      <div className="text-xs text-gray-500 uppercase tracking-wider font-bold w-20 text-center">
                        {tank.gateway_id ? "Awaiting data" : "Not linked"}
                      </div>
                    )}
                    <div className="text-xs space-y-2 flex-1">
                      <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px] tracking-wider">Tank Type</div><div className="font-semibold">{tank.meta?.tank_type || tank.system || "Standard"}</div></div>
                      <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px] tracking-wider">Capacity</div><div className="font-semibold">{capacity.toLocaleString()} L</div></div>
                      <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px] tracking-wider">Location</div><div className="font-semibold">{tank.location_label || tank.location || "—"}</div></div>
                      <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px] tracking-wider">Status</div><div className={`font-semibold ${tank.status === "active" ? "text-emerald-700" : "text-amber-700"}`}>{tank.status?.toUpperCase() || "ACTIVE"}</div></div>
                      {tank.last_tested && <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px] tracking-wider">Last Tested</div><div className="font-semibold text-[11px]">{tank.last_tested.slice(0, 10)}</div></div>}
                    </div>
                  </div>
                  {/* Low/High/Critical-Low Level Detection — computed from the tank's existing
                      live level % against its own existing threshold fields, not a new register
                      link. Each only shown if that threshold was actually set, i.e. "activated"
                      individually per tank. */}
                  {level !== null && (tank.meta?.level_low_pct || tank.meta?.level_high_pct || tank.meta?.level_low_low_pct) && (
                    <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                      {tank.meta?.level_low_low_pct && (
                        <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px] tracking-wider">Critical Low</div><div className={`font-semibold ${level <= Number(tank.meta.level_low_low_pct) ? "text-red-600" : "text-emerald-600"}`}>{level <= Number(tank.meta.level_low_low_pct) ? "DETECTED" : "Normal"}</div></div>
                      )}
                      {tank.meta?.level_low_pct && (
                        <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px] tracking-wider">Low Level</div><div className={`font-semibold ${level <= Number(tank.meta.level_low_pct) ? "text-amber-600" : "text-emerald-600"}`}>{level <= Number(tank.meta.level_low_pct) ? "DETECTED" : "Normal"}</div></div>
                      )}
                      {tank.meta?.level_high_pct && (
                        <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px] tracking-wider">High Level</div><div className={`font-semibold ${level >= Number(tank.meta.level_high_pct) ? "text-amber-600" : "text-emerald-600"}`}>{level >= Number(tank.meta.level_high_pct) ? "DETECTED" : "Normal"}</div></div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {!tanks.length && (
        <div className="glass rounded-lg p-10 text-center text-gray-500 text-sm" data-testid="no-tanks-message">
          No tanks registered yet. <a href="/registry/tanks" className="text-orange-600 hover:text-orange-700 font-semibold">Add tanks in Registry</a>
        </div>
      )}

      {trendTank && hist.length > 0 && (
        <div className="glass p-5 rounded-lg">
          <h3 className="font-semibold mb-3">{trendTank.name} — Level History (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={hist}>
              <CartesianGrid stroke="rgba(0,0,0,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="day" stroke="#94a3b8" fontSize={10} />
              <YAxis domain={[0, 100]} stroke="#94a3b8" fontSize={10} />
              <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#1f2937" }} formatter={(v) => [`${Number(v).toFixed(1)}%`, ""]} />
              <Legend />
              <ReferenceArea y1={0} y2={50} fill="#ef4444" fillOpacity={0.06} />
              <ReferenceLine y={50} stroke="#ff3366" strokeDasharray="5 5" label={{ value: "Critical 50%", position: "right", fill: "#ff3366", fontSize: 10 }} />
              <Line type="monotone" dataKey="value" name="Level %" stroke="#4fd1ff" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {tanks.length > 0 && (
        <ComplianceTable
          title="Section B — Fire Water Tanks (GR Appendix c)"
          rows={tanks.map((tank) => {
            const live = liveValues[tank.asset_id];
            const level = live ? Number(live.value) : null;
            const minPct = Number(tank.meta?.min_level_pct || 50);
            return {
              parameter: tank.name,
              current: level !== null ? `${level.toFixed(1)}%` : "—",
              required: `≥ ${minPct}%`,
              // null = no live data → shown as UNKNOWN; false = below threshold → CRITICAL
              ok: level !== null ? level >= minPct : null,
            };
          })}
        />
      )}
    </div>
  );
}
